import { Toaster } from "@/components/ui/toaster";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import Index from "./pages/Index";
import { ProtectedRoute } from "./components/ProtectedRoute";

// --- Pages publiques (chargées à la demande) ---
const MonApproche = lazy(() => import("./pages/MonApproche"));
const Contact = lazy(() => import("./pages/Contact"));
const Certification = lazy(() => import("./pages/Certification"));
const Tarifs = lazy(() => import("./pages/Tarifs"));
const NewsletterConfirm = lazy(() => import("./pages/NewsletterConfirm"));
const GiftCardSuccess = lazy(() => import("./pages/GiftCardSuccess"));
const ProductSuccess = lazy(() => import("./pages/ProductSuccess"));
const BookingPaymentReturn = lazy(() => import("./pages/BookingPaymentReturn"));

const GDriveCallback = lazy(() => import("./pages/GDriveCallback"));
const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));

// --- Pages SEO ---
const NaturopatheMontesquieu = lazy(() => import("./pages/seo/NaturopatheMontesquieu"));
const NaturopatheVillefranche = lazy(() => import("./pages/seo/NaturopatheVillefranche"));
const NaturopatheStress = lazy(() => import("./pages/seo/NaturopatheStress"));
const NaturopatheDigestion = lazy(() => import("./pages/seo/NaturopatheDigestion"));
const NaturopatheSommeil = lazy(() => import("./pages/seo/NaturopatheSommeil"));
const NaturopatheFatigue = lazy(() => import("./pages/seo/NaturopatheFatigue"));
const FleursDeBach = lazy(() => import("./pages/seo/FleursDeBach"));
const TdahDysEnfants = lazy(() => import("./pages/seo/TdahDysEnfants"));
const ParentaliteBurnout = lazy(() => import("./pages/seo/ParentaliteBurnout"));
const ConsultationsVisio = lazy(() => import("./pages/seo/ConsultationsVisio"));
const NaturopatheToulouse = lazy(() => import("./pages/seo/NaturopatheToulouse"));
const NaturopatheDomicile = lazy(() => import("./pages/seo/NaturopatheDomicile"));

// --- Espace pro (jamais chargé pour un visiteur) ---
const Dashboard = lazy(() => import("./pages/Dashboard"));
const StatsDetail = lazy(() => import("./pages/StatsDetail"));
const FiscalDashboard = lazy(() => import("./pages/FiscalDashboard"));
const NaturopathyApp = lazy(() => import("./pages/NaturopathyApp"));
const PatientsList = lazy(() => import("./pages/PatientsList"));
const PatientFile = lazy(() => import("./pages/PatientFile"));
const TreatmentsLibrary = lazy(() => import("./pages/TreatmentsLibrary"));
const VitalityAssessment = lazy(() => import("./pages/VitalityAssessment"));
const VitalityScoring = lazy(() => import("./pages/VitalityScoring"));
const MedicinalPlantsLibrary = lazy(() => import("./pages/MedicinalPlantsLibrary"));
const BachFlowersLibrary = lazy(() => import("./pages/BachFlowersLibrary"));
const ImportPlants = lazy(() => import("./pages/ImportPlants"));
const ImportBachFlowers = lazy(() => import("./pages/ImportBachFlowers"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const Appointments = lazy(() => import("./pages/Appointments"));
const AvailabilitySettings = lazy(() => import("./pages/AvailabilitySettings"));
const Billing = lazy(() => import("./pages/Billing"));
const EventsManagement = lazy(() => import("./pages/EventsManagement"));
const ProductsManagement = lazy(() => import("./pages/ProductsManagement"));
const AccompanimentsManagement = lazy(() => import("./pages/AccompanimentsManagement"));
const SessionReport = lazy(() => import("./pages/SessionReport"));
const SeasonalWellnessManagement = lazy(() => import("./pages/SeasonalWellnessManagement"));
const GiftCardManagement = lazy(() => import("./pages/GiftCardManagement"));
const ClayLibrary = lazy(() => import("./pages/ClayLibrary"));
const AromatherapyLibrary = lazy(() => import("./pages/AromatherapyLibrary"));
const GemmotherapyLibrary = lazy(() => import("./pages/GemmotherapyLibrary"));
const VegetableOilsLibrary = lazy(() => import("./pages/VegetableOilsLibrary"));
const HydrolatsLibrary = lazy(() => import("./pages/HydrolatsLibrary"));
const ApitherapyLibrary = lazy(() => import("./pages/ApitherapyLibrary"));
const IridologyDashboard = lazy(() => import("./pages/IridologyDashboard"));
const IridologySession = lazy(() => import("./pages/IridologySession"));
const NewsletterManagement = lazy(() => import("./pages/NewsletterManagement"));
const SocialPublishing = lazy(() => import("./pages/SocialPublishing"));
const LocationsManagement = lazy(() => import("./pages/LocationsManagement"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-gradient-hero flex items-center justify-center">
    <div className="text-primary font-montserrat text-sm">Chargement…</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineIndicator />
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/auth" element={<Auth />} />
            <Route path="/" element={<Index />} />
            <Route path="/mon-approche" element={<MonApproche />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/certification" element={<Certification />} />
            <Route path="/tarifs" element={<Tarifs />} />
            <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/stats/:type" element={<ProtectedRoute><StatsDetail /></ProtectedRoute>} />
            <Route path="/fiscal" element={<ProtectedRoute><FiscalDashboard /></ProtectedRoute>} />
            <Route path="/app" element={<ProtectedRoute><NaturopathyApp /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><TreatmentsLibrary /></ProtectedRoute>} />
            <Route path="/treatments-library" element={<ProtectedRoute><TreatmentsLibrary /></ProtectedRoute>} />
            <Route path="/clay-library" element={<ProtectedRoute><ClayLibrary /></ProtectedRoute>} />
            <Route path="/aromatherapy-library" element={<ProtectedRoute><AromatherapyLibrary /></ProtectedRoute>} />
            <Route path="/gemmotherapy-library" element={<ProtectedRoute><GemmotherapyLibrary /></ProtectedRoute>} />
            <Route path="/vegetable-oils-library" element={<ProtectedRoute><VegetableOilsLibrary /></ProtectedRoute>} />
            <Route path="/hydrolats-library" element={<ProtectedRoute><HydrolatsLibrary /></ProtectedRoute>} />
            <Route path="/apitherapy-library" element={<ProtectedRoute><ApitherapyLibrary /></ProtectedRoute>} />
            <Route path="/plants" element={<ProtectedRoute><MedicinalPlantsLibrary /></ProtectedRoute>} />
            <Route path="/bach-flowers" element={<ProtectedRoute><BachFlowersLibrary /></ProtectedRoute>} />
            <Route path="/import-plants" element={<ProtectedRoute><ImportPlants /></ProtectedRoute>} />
            <Route path="/import-bach-flowers" element={<ProtectedRoute><ImportBachFlowers /></ProtectedRoute>} />
            <Route path="/patients" element={<ProtectedRoute><PatientsList /></ProtectedRoute>} />
            <Route path="/patients/:id" element={<ProtectedRoute><PatientFile /></ProtectedRoute>} />
            <Route path="/appointments" element={<ProtectedRoute><Appointments /></ProtectedRoute>} />
            <Route path="/availability-settings" element={<ProtectedRoute><AvailabilitySettings /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/events-management" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
            <Route path="/products-management" element={<ProtectedRoute><ProductsManagement /></ProtectedRoute>} />
            <Route path="/accompaniments-management" element={<ProtectedRoute><AccompanimentsManagement /></ProtectedRoute>} />
            <Route path="/vitality" element={<ProtectedRoute><VitalityAssessment /></ProtectedRoute>} />
            <Route path="/vitality/:id" element={<ProtectedRoute><VitalityAssessment /></ProtectedRoute>} />
            <Route path="/vitality-scoring" element={<ProtectedRoute><VitalityScoring /></ProtectedRoute>} />
            <Route path="/session-report" element={<ProtectedRoute><SessionReport /></ProtectedRoute>} />
            <Route path="/seasonal-wellness" element={<ProtectedRoute><SeasonalWellnessManagement /></ProtectedRoute>} />
            <Route path="/gift-cards" element={<ProtectedRoute><GiftCardManagement /></ProtectedRoute>} />
            <Route path="/gift-card-success" element={<GiftCardSuccess />} />
            <Route path="/product-success" element={<ProductSuccess />} />
            <Route path="/paiement-rendez-vous" element={<BookingPaymentReturn />} />

            <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
            <Route path="/iridology" element={<ProtectedRoute><IridologyDashboard /></ProtectedRoute>} />
            <Route path="/iridology/:id" element={<ProtectedRoute><IridologySession /></ProtectedRoute>} />
            <Route path="/newsletter-management" element={<ProtectedRoute><NewsletterManagement /></ProtectedRoute>} />
            <Route path="/social-publishing" element={<ProtectedRoute><SocialPublishing /></ProtectedRoute>} />
            <Route path="/locations" element={<ProtectedRoute><LocationsManagement /></ProtectedRoute>} />
            <Route path="/gdrive-callback" element={<GDriveCallback />} />
            {/* SEO public pages */}
            <Route path="/naturopathe-montesquieu-lauragais" element={<NaturopatheMontesquieu />} />
            <Route path="/naturopathe-villefranche-de-lauragais" element={<NaturopatheVillefranche />} />
            <Route path="/naturopathe-stress" element={<NaturopatheStress />} />
            <Route path="/naturopathe-digestion" element={<NaturopatheDigestion />} />
            <Route path="/naturopathe-sommeil" element={<NaturopatheSommeil />} />
            <Route path="/naturopathe-fatigue" element={<NaturopatheFatigue />} />
            <Route path="/fleurs-de-bach" element={<FleursDeBach />} />
            <Route path="/tdah-dys-enfants-atypiques" element={<TdahDysEnfants />} />
            <Route path="/parentalite-burn-out-parental" element={<ParentaliteBurnout />} />
            <Route path="/consultations-visio-naturopathe-france" element={<ConsultationsVisio />} />
            <Route path="/naturopathe-toulouse" element={<NaturopatheToulouse />} />
            <Route path="/naturopathe-domicile-toulouse-lauragais" element={<NaturopatheDomicile />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
